// @ts-nocheck -- copied verbatim from the application being replaced.
//
// Quarantined, not exempted. This file has not been rewritten to this project's
// settings and does not pass them; checking it would report errors nobody is
// going to fix in place, and loosening the settings to accommodate it would
// apply that to code written here too. Vite still compiles it -- esbuild strips
// types without checking them.
//
// Delete this line when the file is rewritten. It is the marker for what is left
// to do, and the count of files carrying it is the size of the refactor.
import React, { createContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { User, AuthContextType } from '../utils/auth';
import UnifiedAuthStorage, { handleLogout } from '../services/unified-auth.service';
import { logger } from '@/shared/observability/logger';

interface AuthProviderProps {
 readonly children: ReactNode;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAgencyUser, setIsAgencyUser] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Add loading state

 
  // Initialize auth state from unified storage on mount
  useEffect(() => {
    logger.debug('🔄 [AuthProvider] Initializing auth state...');
    
    // Check for and migrate legacy storage
    const migrated = UnifiedAuthStorage.migrateLegacyStorage();
    if (migrated) {
      logger.debug('⚠️ Legacy storage migrated - user needs to re-login');
      setIsLoading(false);
      return;
    }

    // Load auth state from unified storage
    if (UnifiedAuthStorage.isAuthenticated()) {
      const savedUser = UnifiedAuthStorage.getUser();
      if (savedUser) {
        setIsAuthenticated(true);
        setUser(savedUser);
        setIsAgencyUser(savedUser.isAgencyUser ?? false);
        logger.debug('✅ Auth state restored from unified storage:', {
          userId: savedUser.id,
          username: savedUser.username,
          email: savedUser.email,
          isAgencyUser: savedUser.isAgencyUser
        });
      } else {
        logger.warn('⚠️ UnifiedAuthStorage reports authenticated but no user data found');
      }
    } else {
      logger.debug('ℹ️ No valid auth session found');
    }
    
    // Mark loading as complete
    setIsLoading(false);
    logger.debug('✅ [AuthProvider] Auth state initialization complete');
  }, []);

  const login = (userData: User) => {
    setIsAuthenticated(true);
    setUser(userData);
    setIsAgencyUser(userData.isAgencyUser ?? false);
    logger.debug('✅ User logged in via AuthProvider');
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setIsAgencyUser(false);
    handleLogout();
    logger.debug(' User logged out via AuthProvider');
  };

  const refreshUser = () => {
    const savedUser = UnifiedAuthStorage.getUser();
    if (savedUser) {
      setUser(savedUser);
      setIsAgencyUser(savedUser.isAgencyUser ?? false);
      logger.debug('✅ User data refreshed from storage');
    }
  };

  const value: AuthContextType = useMemo(() => ({
    user,
    isAuthenticated,
    isAgencyUser,
    isLoading,
    login,
    logout,
    refreshUser
  }), [user, isAuthenticated, isAgencyUser, isLoading, login, logout, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook for easier access to auth context
export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};